// The bell's inbox re-read — rows and badge — driven by push (plan 101 E3).
//
// The badge is the API's account-wide `unread_count`, and only a REST read of
// `GET /v1/notifications` carries it, and that read is also the ONLY source of
// the rows the panel lists (`useNotificationInbox().refresh`, `paged.reload()`).
// The account stream's `notifications` topic says WHEN: before this, nothing
// re-read the inbox after mount, and the badge stayed where the mount read left
// it until a reload. This re-reads exactly when the stream says something moved,
// and never on a timer:
//
//   - a row the stream serves that is NEWS: one this tab saw at another
//     `updated_at` (a delivery status transition, or a read stamped on another
//     tab or device — `markNotificationRead` bumps `updated_at`), or one it has
//     never seen whose `updated_at` is past the WATERMARK — the newest
//     `updated_at` among the rows the inbox already holds (REST pages and
//     stream rows alike). A new notification, or a change to an older row past
//     the head, lands past it. An inbox whose mount read LANDED EMPTY has no
//     watermark, so every row it has never seen is past it: the first
//     notification an empty inbox receives used to be judged against a
//     watermark of '' and dropped as "not news", leaving the badge at 0 until a
//     reload (#5608 slice 17 row 5);
//   - a RECONNECT (`reconnecting` -> `live`), once. The replayed snapshot is only
//     the bounded head, so a change that left the head during the gap would
//     move the count with no row here to say so.
//
// The watermark is what keeps a MOUNT at one read. The shell mounts the bell on
// every page load, and the first snapshot replays the whole head (~100 rows):
// the mount read already saw that state, so those rows seed `seen` instead of
// re-reading. Both sides of the comparison are the DB's own `updated_at`, never
// the browser clock. If the stream beats the mount read (`settled` still false),
// its rows seed silently too — the mount read is still in flight and answers
// with that state.
//
// Every trigger inside one synchronous frame batch coalesces to ONE read: a
// snapshot is ~100 patches, applied in one loop (`applyAccountFrames`).
//
// `seen` is not recovery bookkeeping — the rows on screen come from the REST
// re-read this triggers, never from the stream. It only answers "is this patch
// news". It holds one short entry per notification id this tab has been shown.

import { createEffect, on } from 'solid-js';
import type { AccountStreamStatus } from './accountStream';
import type { NotificationRow } from './notifications';

export function refreshInboxOnPush(input: {
  /** The `notifications` topic's rows (validated upstream by `acceptPatch`). */
  readonly streamRows: () => readonly unknown[];
  readonly status: () => AccountStreamStatus;
  /** The rows the inbox's REST pages hold — what the mount read already saw. */
  readonly fetchedRows: () => readonly NotificationRow[];
  /**
   * The mount read has landed — possibly EMPTY. `fetchedRows()` alone cannot
   * tell "not landed" from "landed with nothing"; `useNotificationInbox().settled`.
   */
  readonly settled: () => boolean;
  /** Re-read the inbox: the only source of `unread_count`. */
  readonly refresh: () => void;
  /**
   * Fired at most once per stream batch when the batch holds a row this tab
   * has never seen whose `updated_at` is past the watermark and whose
   * `read_at == null` — the narrower question than `news`, which also fires
   * for a status transition and a read stamped on another tab or device. NOT
   * fired on a reconnect, NOT fired before the mount read has landed, and
   * NOT fired for the replayed first snapshot. Optional; the bell wires it
   * to the stored sound. (`#5890`.)
   */
  readonly onArrival?: () => void;
}): void {
  const seen = new Map<string, string>();
  const held = new Set<string>();
  let scheduled = false;
  const schedule = (): void => {
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      input.refresh();
    });
  };

  // ISO-8601 from one serializer, so string order is time order.
  let watermark = '';
  const raise = (at: string): void => {
    if (at > watermark) watermark = at;
  };

  createEffect(() => {
    for (const n of input.fetchedRows()) raise(n.updated_at);
    // A row the inbox already holds is not an arrival when the stream serves it
    // again, even at a fresher `updated_at` (a status transition): `held` is read
    // by the arrival test only, so `news` below keeps its own meaning (#5890).
    for (const n of input.fetchedRows()) held.add(n.id);
    const landed = input.settled();
    let news = false;
    let arrived = false;
    for (const r of input.streamRows()) {
      const n = r as NotificationRow;
      const was = seen.get(n.id);
      if (was === n.updated_at) continue;
      if (was !== undefined || (landed && n.updated_at > watermark)) news = true;
      // An arrival is a row this tab has never seen, past the watermark, still
      // unread. A `news` row that was seen at another `updated_at` (a status
      // transition or a read stamped on another tab — `markNotificationRead`
      // bumps `updated_at`) is NOT an arrival. A `news` row whose stream
      // beat the mount read (settled false) is also not one. Called once
      // per batch, after the pass.
      if (
        was === undefined &&
        !held.has(n.id) &&
        landed &&
        n.updated_at > watermark &&
        n.read_at == null
      ) {
        arrived = true;
      }
      seen.set(n.id, n.updated_at);
    }
    // Raised AFTER the pass: one batch's rows are judged against what was held
    // before it, not against each other.
    for (const [, at] of seen) raise(at);
    if (news) schedule();
    if (arrived) input.onArrival?.();
  });

  createEffect(
    on(input.status, (now, prev) => {
      if (now === 'live' && prev === 'reconnecting') schedule();
    }),
  );
}
